import {
  DataTableProps,
  EmptyStateProps,
  SortableTableProps,
  SimplePaginationProps,
  TableStyleProps,
} from '@dixa/lib-shared-habla';
import type {
  SearchConversationEnriched,
  SearchSortField,
} from 'lib-search-types';
import { AsyncStatus } from 'lib-shared-types-api';
import type { ReactNode } from 'react';

import type { UseConversationsTableLayoutResult } from './utils/useConversationsTableLayout';

export type ConversationsTableProps = {
  conversations: readonly SearchConversationEnriched[];
  asyncStatus: AsyncStatus;
  selectableRowsProps?: DataTableProps<
    SearchConversationEnriched,
    never
  >['selectableRowsProps'];
  paginationProps?: SimplePaginationProps;
  sortingProps?: SortableTableProps;
  /** Overrides the default empty state shown when there are no conversations. */
  emptyStateProps?: Partial<EmptyStateProps>;
  /** When provided, replaces the default navigation on row click. */
  onRowClick?: (conversation: SearchConversationEnriched) => void;
  /** When provided, renders a row's action in place of the button that previews the conversation. */
  renderAction?: (conversation: SearchConversationEnriched) => ReactNode;
  /** The id of the conversation whose row should be highlighted, e.g. when it's being previewed. */
  highlightedRowId?: number;
  /** Whether the user can choose the columns, saved to their user storage. */
  isCustomizable?: boolean;
} & TableStyleProps;

export type ConversationsTableSortableField =
  (typeof conversationsTableSortableFields)[number];

export const conversationsTableSortableFields = [
  'created_at',
  'last_message_created_at',
  'queued_at',
  'pending_expiration_at',
  'language',
  'inbound_message_count',
  'non_automated_outbound_message_count',
] satisfies SearchSortField<'conversations'>[];

/** How a conversations table column is rendered with the Habla table. */
export type ConversationsTableColumnMeta = {
  /** The tooltip shown on the column header. */
  readonly tooltip?: string;
  /** The width of the column in a table the user can't customize; defaults to the column size. */
  readonly width?: string;
  /** Whether the column is hidden until the user chooses to show it. */
  readonly isHiddenByDefault?: boolean;
  /** Whether the cells cut off their content with an ellipsis in compact rows; defaults to true. */
  readonly truncate?: boolean;
  /** The id the table sorts by when the header is clicked. */
  readonly sortBy?: ConversationsTableSortableField;
  /** What the column shows before the first load. */
  readonly placeholder?: {
    /** The width of the header placeholder. */
    readonly header?: string;
    /** The content of the placeholder rows' cells. */
    readonly cell?: ReactNode;
  };
};

/** How much space the rows of the conversations table take up. */
export type ConversationsTableDensity = 'default' | 'compact';

/** What the conversations table passes to its header and cell renderers. */
export type ConversationsTableMeta = {
  /** The user's saved layout, when they can customize the table. */
  readonly layout?: UseConversationsTableLayoutResult;
};
